import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, Edit, Trash2, Users, CheckCircle, XCircle } from "lucide-react";

const ROLES = ["attendant", "nurse", "cleaner", "lab_assistant", "receptionist", "security", "admin_staff"];
const SHIFTS = ["morning", "afternoon", "night"];
const EMPTY = { name: "", email: "", phone: "", role: "attendant", department_id: "", shift: "morning", password: "Staff@123" };

export default function Staff() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [staff, setStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/staff"), api.get("/departments")]).then(([s, d]) => {
      setStaff(s.data); setDepartments(d.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const fetchStaff = async () => {
    const { data } = await api.get("/staff");
    setStaff(data);
  };

  const openAdd = () => { setForm(EMPTY); setEditStaff(null); setShowModal(true); };
  const openEdit = (s) => { setForm({...s, password: ""}); setEditStaff(s); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editStaff) {
        const { password, ...upd } = form;
        await api.put(`/staff/${editStaff.id}`, upd);
        toast({ title: "Staff updated" });
      } else {
        await api.post("/staff", form);
        toast({ title: "Staff added" });
      }
      setShowModal(false); fetchStaff();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this staff member?")) return;
    try {
      await api.delete(`/staff/${id}`);
      toast({ title: "Staff deleted" });
      fetchStaff();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const markAttendance = async (status) => {
    try {
      await api.post(`/staff/${selectedStaff.id}/attendance`, {
        date: new Date().toISOString().split("T")[0],
        status,
        check_in: status === "present" ? new Date().toTimeString().slice(0,5) : null,
      });
      toast({ title: `Attendance marked: ${status}` });
      setShowAttendance(false);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const filtered = staff.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.role?.toLowerCase().includes(search.toLowerCase())
  );
  const getDept = (id) => departments.find(d => d.id === id)?.name || "-";

  const shiftColors = { morning: "bg-amber-100 text-amber-800", afternoon: "bg-blue-100 text-blue-800", night: "bg-violet-100 text-violet-800" };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">{t("staff")}</h1>
          <p className="text-slate-500 text-sm mt-1">Manage clinic staff and attendance</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={openAdd} data-testid="add-staff-btn">
          <Plus size={16} /> Add Staff
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search staff..." className="pl-9 border-slate-200" value={search} onChange={e => setSearch(e.target.value)} data-testid="staff-search-input" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-700">Name</TableHead>
              <TableHead className="font-semibold text-slate-700">Role</TableHead>
              <TableHead className="font-semibold text-slate-700">Department</TableHead>
              <TableHead className="font-semibold text-slate-700">Shift</TableHead>
              <TableHead className="font-semibold text-slate-700">Phone</TableHead>
              <TableHead className="font-semibold text-slate-700">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">{t("loading")}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                <Users size={32} className="mx-auto mb-2 opacity-30" />
                <p>No staff found</p>
              </TableCell></TableRow>
            ) : filtered.map(s => (
              <TableRow key={s.id} className="table-row-hover" data-testid={`staff-row-${s.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-violet-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-violet-800">{s.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{s.name}</p>
                      <p className="text-xs text-slate-500">{s.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell><Badge variant="outline" className="capitalize text-xs">{s.role?.replace("_", " ")}</Badge></TableCell>
                <TableCell className="text-sm text-slate-600">{getDept(s.department_id)}</TableCell>
                <TableCell><Badge className={`capitalize text-xs ${shiftColors[s.shift] || "bg-slate-100 text-slate-700"}`}>{s.shift}</Badge></TableCell>
                <TableCell className="text-sm text-slate-600">{s.phone}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600 hover:bg-emerald-50" onClick={() => { setSelectedStaff(s); setShowAttendance(true); }} data-testid={`attendance-btn-${s.id}`}>
                      Attendance
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`}>
                      <Edit size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-rose-600" onClick={() => handleDelete(s.id)} data-testid={`delete-staff-${s.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-sans">{editStaff ? "Edit Staff" : "Add Staff Member"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-sm text-slate-700">Full Name</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 border-slate-200" required data-testid="staff-name-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 border-slate-200" required data-testid="staff-email-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 border-slate-200" required data-testid="staff-phone-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Role</Label>
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white capitalize" data-testid="staff-role-select">
                  {ROLES.map(r => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Shift</Label>
                <select value={form.shift} onChange={e => setForm({...form, shift: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white capitalize">
                  {SHIFTS.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Department</Label>
                <select value={form.department_id} onChange={e => setForm({...form, department_id: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                  <option value="">Select Dept</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              {!editStaff && (
                <div>
                  <Label className="text-sm text-slate-700">Password</Label>
                  <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="mt-1 border-slate-200" required />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving} data-testid="save-staff-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Attendance Modal */}
      <Dialog open={showAttendance} onOpenChange={setShowAttendance}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-sans">Mark Attendance — {selectedStaff?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <p className="text-sm text-slate-500">Today: {new Date().toLocaleDateString()}</p>
            <div className="grid grid-cols-2 gap-3">
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={() => markAttendance("present")} data-testid="mark-present-btn">
                <CheckCircle size={16} /> Present
              </Button>
              <Button variant="outline" className="border-rose-200 text-rose-600 hover:bg-rose-50 gap-2" onClick={() => markAttendance("absent")} data-testid="mark-absent-btn">
                <XCircle size={16} /> Absent
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
